import { cn } from '../../lib/helpers';
import {
    Dimension,
    DIMENSION_LABELS,
    DIMENSION_STYLES,
    DIMENSIONS,
} from '../../lib/plan/types';

interface RadarChartProps {
    scores: Record<Dimension, number>;
    primary: Dimension;
    previousScores?: Record<Dimension, number>;
}

const MAX_SCORE = 4;
const CENTER_X = 170;
const CENTER_Y = 100;
const RADIUS = 70;

// pořadí paprsků: koncentrace nahoře, atmosféra vpravo, vztahy dole, emoce vlevo
const ANGLES: Record<Dimension, number> = {
    koncentrace: -90,
    atmosfera: 0,
    vztahy: 90,
    emoce: 180,
};

function point(dimension: Dimension, value: number) {
    const angle = (ANGLES[dimension] * Math.PI) / 180;
    const r = (value / MAX_SCORE) * RADIUS;
    return {
        x: CENTER_X + r * Math.cos(angle),
        y: CENTER_Y + r * Math.sin(angle),
    };
}

function formatScore(value: number) {
    return value.toFixed(1).replace('.', ',');
}

export function PlanRadarChart({ scores, primary, previousScores }: RadarChartProps) {
    const dataPoints = DIMENSIONS.map((dim) => point(dim, scores[dim]));
    const polygon = dataPoints.map((p) => `${p.x},${p.y}`).join(' ');

    const previousPolygon = previousScores
        ? DIMENSIONS.map((dim) => {
              const p = point(dim, previousScores[dim]);
              return `${p.x},${p.y}`;
          }).join(' ')
        : null;

    const grid = [0.5, 1].map((fraction) =>
        DIMENSIONS.map((dim) => {
            const p = point(dim, MAX_SCORE * fraction);
            return `${p.x},${p.y}`;
        }).join(' ')
    );

    const labels: Record<
        Dimension,
        { x: number; y: number; anchor: 'start' | 'middle' | 'end' }
    > = {
        koncentrace: { x: CENTER_X, y: CENTER_Y - RADIUS - 14, anchor: 'middle' },
        atmosfera: { x: CENTER_X + RADIUS + 10, y: CENTER_Y + 4, anchor: 'start' },
        vztahy: { x: CENTER_X, y: CENTER_Y + RADIUS + 22, anchor: 'middle' },
        emoce: { x: CENTER_X - RADIUS - 10, y: CENTER_Y + 4, anchor: 'end' },
    };

    return (
        <svg
            viewBox="0 0 340 205"
            className="w-full max-w-[340px]"
            role="img"
            aria-label="Pavučinový graf čtyř dimenzí třídy"
        >
            {grid.map((points) => (
                <polygon
                    key={points}
                    points={points}
                    fill="none"
                    className="stroke-cloud-300"
                />
            ))}
            {DIMENSIONS.map((dim) => {
                const outer = point(dim, MAX_SCORE);
                return (
                    <line
                        key={dim}
                        x1={CENTER_X}
                        y1={CENTER_Y}
                        x2={outer.x}
                        y2={outer.y}
                        className="stroke-cloud-300"
                    />
                );
            })}
            {previousPolygon && (
                <polygon
                    points={previousPolygon}
                    fill="none"
                    strokeDasharray="4 3"
                    strokeWidth={1.5}
                    className="stroke-ink-700/50"
                />
            )}
            {/* Výplň zůstává neutrální — obarvit i plochu by z grafu udělalo
                duhu. Barvu nesou jen vrcholy a popisky os. */}
            <polygon
                points={polygon}
                className="fill-product-100/90 stroke-product-500"
                strokeWidth={2.5}
            />
            {DIMENSIONS.map((dim) => {
                const vertex = point(dim, scores[dim]);
                const isPrimary = dim === primary;
                return (
                    <circle
                        key={dim}
                        cx={vertex.x}
                        cy={vertex.y}
                        r={isPrimary ? 5.5 : 4}
                        strokeWidth={isPrimary ? 2 : 0}
                        className={cn(
                            DIMENSION_STYLES[dim].dot,
                            isPrimary && 'stroke-ink-900/25'
                        )}
                    />
                );
            })}
            {DIMENSIONS.map((dim) => {
                const label = labels[dim];
                return (
                    <text
                        key={dim}
                        x={label.x}
                        y={label.y}
                        textAnchor={label.anchor}
                        className="fill-ink-800 text-[12px] font-semibold"
                    >
                        {DIMENSION_LABELS[dim]} {formatScore(scores[dim])}
                    </text>
                );
            })}
        </svg>
    );
}
